import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IceCream, ChevronDown } from 'lucide-react';
import { useCategories } from '../../hooks/useCategories';
import { useProducts } from '../../hooks/useProducts';
import './Landing.css';

// Import hero images
import heroImage from './assets/hero_gelato_background_1765329114627.png';
import menuBg from './assets/menu_section_background_1765329140576.png';
import { getImageUrl } from '../../utils/url';

const ALL_CATEGORY = 'Todos';

export default function Landing() {
  const [active, setActive] = useState<string>(ALL_CATEGORY);
  const [scrolled, setScrolled] = useState(false);

  const { data: categories = [], isLoading: loadingCategories } = useCategories();
  const { data: products = [], isLoading: loadingProducts } = useProducts();

  // Handle scroll effect for navigation
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filtered =
    active === ALL_CATEGORY
      ? products
      : products.filter((p) =>
        typeof p.category === 'string'
          ? p.category === active
          : p.category?.name === active,
      );


  const scrollToMenu = () => {
    document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ backgroundColor: '#1a1a1a' }}>
      {/* Navigation */}
      <nav className={`elegant-nav ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="nav-logo">
          DUFI GELATO
        </Link>
        <Link to="/login" className="nav-link">
          Sistema
        </Link>
      </nav>

      {/* Hero Section */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-logo">
            <IceCream size={80} color="#d4a574" />
          </div>
          <h1 className="hero-title">DUFI GELATO</h1>
          <p className="hero-subtitle">Heladería & Comidas Rápidas</p>
          <a href="#menu-section" className="hero-cta" onClick={(e) => {
            e.preventDefault();
            scrollToMenu();
          }}>
            Descubre Nuestro Menú
          </a>
        </div>
        <div className="scroll-indicator" onClick={scrollToMenu}>
          <ChevronDown size={32} color="#d4a574" />
        </div>
      </section>

      {/* Menu Title Section */}
      <section
        id="menu-section"
        className="section-title-container"
        style={{ backgroundImage: `url(${menuBg})` }}
      >
        <div className="section-title-overlay"></div>
        <div className="section-title-content">
          <h2 className="section-title">Nuestro Menú</h2>
          <p className="section-subtitle">Descubre Nuestras Delicias</p>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="categories-container">
          {loadingCategories ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton" style={{ height: '40px', width: '120px' }}></div>
              ))}
            </>
          ) : (
            [ALL_CATEGORY, ...categories.map((c) => c.name)].map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`category-chip ${active === c ? 'active' : ''}`}
              >
                {c}
              </button>
            ))
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        {loadingProducts ? (
          <div className="products-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton" style={{ height: '450px', borderRadius: '0' }}></div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="products-grid">
            {filtered.map((product) => {
              const imageUrl = getImageUrl(product.imageUrl as any);

              return (
                <div key={product.id} className="product-card">
                  <div className="product-image-wrapper">
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={product.name}
                          className="product-image"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            if (target.nextElementSibling) {
                              (target.nextElementSibling as HTMLElement).style.display = 'flex';
                            }
                          }}
                        />
                        <div
                          className="product-image"
                          style={{
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #8b6f47 0%, #d4a574 100%)'
                          }}
                        >
                          <IceCream size={80} color="rgba(255,255,255,0.3)" />
                        </div>
                      </>
                    ) : (
                      <div
                        className="product-image"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #8b6f47 0%, #d4a574 100%)'
                        }}
                      >
                        <IceCream size={80} color="rgba(255,255,255,0.3)" />
                      </div>
                    )}
                    <div className="product-overlay"></div>
                    <div className="product-content">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-description">{product.description}</p>
                      <div className="product-price">
                        ${product.price.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <IceCream size={80} color="#8a8a8a" />
            </div>
            <h3 className="empty-title">No hay productos en esta categoría</h3>
            <p className="empty-text">Selecciona otra categoría para ver más productos</p>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="elegant-footer">
        <div className="footer-content">
          <h3 className="footer-title">DUFI GELATO</h3>
          <p className="footer-text">Heladería y Comidas Rápidas</p>
          <p className="footer-text">© 2025 DUFI Gelato - Todos los derechos reservados</p>
          <p className="footer-text" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
            Hecho con pasión para nuestros clientes
          </p>
        </div>
      </footer>
    </div>
  );
}
